import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Container,
  Toolbar,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ThemeProvider,
} from "@mui/material";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import MenuIcon from "@mui/icons-material/Menu";
import { supabase } from "./supabaseClient";
import { getTheme } from "./theme";
import Auth from "./components/Auth";
import FileUpload from "./components/FileUpload";
import TagTransactions from "./components/TagTransactions";
import Report from "./components/Report";
import AuthDebugger from "./components/debugging/AuthDebugger";

// data grid auto height, flex column cause too many resizes
window.addEventListener("error", function (e) {
  if (
    e.message ===
    "ResizeObserver loop completed with undelivered notifications."
  ) {
    console.log(e);
    // prevent React's listener from firing
    e.stopImmediatePropagation();
    // prevent the browser's console error message
    e.preventDefault();
  }
});

function App() {
  const [userSettings, setUserSettings] = useState(() => {
    const savedSettings = localStorage.getItem("userSettings");
    return savedSettings
      ? JSON.parse(savedSettings)
      : {
          themeMode: "light",
          // Add other global settings here in the future
        };
  });
  const mode = userSettings.themeMode;
  const customTheme = getTheme(mode);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [session, setSession] = useState(null);

  const toggleColorMode = () => {
    setUserSettings((prevSettings) => {
      const newSettings = {
        ...prevSettings,
        themeMode: prevSettings.themeMode === "light" ? "dark" : "light",
      };
      localStorage.setItem("userSettings", JSON.stringify(newSettings));
      return newSettings;
    });
  };
  const [currentPage, setCurrentPage] = useState("Upload");
  const [, setCsvData] = useState(() => {
    const savedData = localStorage.getItem("transactionData");
    return savedData ? JSON.parse(savedData) : null;
  });
  const [dataValidated, setDataValidated] = useState(false);

  // Check if there's valid data in localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("transactionData");
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        if (Array.isArray(data) && data.length > 0) {
          // We'll rely on FileUpload component to validate the data
          // and set dataValidated to true when the user clicks "Proceed"
          setCurrentPage("Upload");
        }
      } catch (error) {
        console.error("Error parsing saved transaction data:", error);
      }
    }
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePageChange = (page) => {
    // Prevent navigation to Tag or Report pages if data is not validated
    if ((page === "Tag" || page === "Report") && !dataValidated) {
      return; // Don't change the page
    }

    setCurrentPage(page);
    setMobileOpen(false);
  };

  const handleCsvDataLoaded = (data, additionalData = null) => {
    setCsvData(data);
    setDataValidated(true);
    
    // If additional data is provided (from database load), handle it
    if (additionalData) {
      // Update user settings if provided
      if (additionalData.userSettings) {
        setUserSettings(prevSettings => ({
          ...prevSettings,
          ...additionalData.userSettings
        }));
        localStorage.setItem("userSettings", JSON.stringify({
          ...userSettings,
          ...additionalData.userSettings
        }));
      }
      
      // Store additional data for other components to load
      if (additionalData.rules) {
        localStorage.setItem("savedRules", JSON.stringify(additionalData.rules));
      }
      
      if (additionalData.history) {
        localStorage.setItem("savedHistory", JSON.stringify(additionalData.history));
      }
      
      if (additionalData.filters) {
        localStorage.setItem("tableFilters", JSON.stringify(additionalData.filters));
      }
    }
    
    setCurrentPage("Tag");
  };

  // Add debug option in development mode
  const menuItems = ["Upload", "Tag", "Report"];
  const developmentMenuItems = process.env.NODE_ENV === 'development' ? [...menuItems, "Debug"] : menuItems;

  const drawer = (
    <List>
      {developmentMenuItems.map((item) => (
        <ListItem key={item}>
          <ListItemButton
            onClick={() => handlePageChange(item)}
            disabled={(item === "Tag" || item === "Report") && !dataValidated}
          >
            <ListItemText
              primary={item}
              sx={{
                color:
                  (item === "Tag" || item === "Report") && !dataValidated
                    ? "text.disabled"
                    : "inherit",
              }}
            />
          </ListItemButton>
        </ListItem>
      ))}
      <ListItem>
        <ListItemButton onClick={() => supabase.auth.signOut()}>
          <ListItemText primary="Sign Out" />
        </ListItemButton>
      </ListItem>
    </List>
  );

  const renderContent = () => {
    switch (currentPage) {
      case "Upload":
        return <FileUpload onCsvDataLoaded={handleCsvDataLoaded} />;
      case "Tag":
        return <TagTransactions userEmail={session?.user?.email} userSettings={userSettings} />;
      case "Report":
        return <Report />;
      case "Debug":
        return <AuthDebugger />;
      default:
        return (
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((item) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={item}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" component="div">
                      Card {item}
                    </Typography>
                    <Typography variant="body2">
                      This is a responsive card that adjusts its size based on
                      the screen width. It takes full width on mobile, half
                      width on tablets, and quarter width on desktop.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ minHeight: "100vh", flexGrow: 1 }}>
        {!session ? (
          <Auth />
        ) : (
          <>
            <AppBar position="static">
              <Toolbar>
                {isMobile && (
                  <IconButton
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ mr: 2 }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
                <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
                  TransTags
                </Typography>
                <IconButton sx={{ ml: 1 }} onClick={toggleColorMode}>
                  {userSettings.themeMode === "dark" ? (
                    <Brightness4Icon />
                  ) : (
                    <Brightness7Icon />
                  )}
                </IconButton>
                {!isMobile && (
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {developmentMenuItems.map((item) => (
                      <Typography
                        key={item}
                        role="menuitem"
                        sx={{
                          color:
                            currentPage === item
                              ? "secondary.main"
                              : (item === "Tag" || item === "Report") &&
                                  !dataValidated
                                ? "text.disabled"
                                : "inherit",
                          cursor:
                            (item === "Tag" || item === "Report") &&
                            !dataValidated
                              ? "not-allowed"
                              : "pointer",
                        }}
                        onClick={() => handlePageChange(item)}
                      >
                        {item}
                      </Typography>
                    ))}
                    <Typography
                      key={"Sign Out"}
                      role="menuitem"
                      sx={{
                        color: "inherit",
                      }}
                      onClick={() => supabase.auth.signOut()}
                    >
                      Sign Out
                    </Typography>
                  </Box>
                )}
              </Toolbar>
            </AppBar>

            <Drawer
              variant="temporary"
              anchor="left"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
            >
              {drawer}
            </Drawer>

            <Container
              maxWidth={false}
              sx={{
                minHeight: "100vh",
              }}
            >
              {renderContent()}
            </Container>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default App;
